import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, Ban, Download, Filter, Loader2, RefreshCw, X,
} from 'lucide-react';
import ImagemSharePoint from '../../ImagemSharePoint';

// =====================================================================
// FINANCEIRO — Registro de Nulos em Progresso: a TELA DE CONTROLE.
//
// Lista os nulos declarados, com o placar do período no topo. É a tela do
// Financeiro, da Auditoria e da Diretoria; o gerente vê a mesma coisa recortada
// na loja dele.
//
// ⚠️ O RECORTE POR LOJA É DO SERVIDOR, não desta tela. Aqui o filtro de loja só
//    ESTREITA o que o backend já devolveu — quem tenta alargar recebe lista
//    vazia, não a loja alheia.
//
// ⚠️ CANCELADO É ESCONDIDO, NUNCA APAGADO: existe o interruptor para mostrá-lo,
//    porque auditar os cancelamentos também é trabalho de quem confere. Ele nunca
//    entra nos totais — somar o que já foi anulado inflaria o valor da loja.
// =====================================================================

const API = '/api/financeiro/nulos';
// Paleta do módulo FINANCEIRO (verde-petróleo). ⚠️ Era o laranja da Auditoria até
// 28/08/2026 — tela dentro de um módulo usa a cor DELE (docs/ARQUITETURA.md ›
// Paleta dos módulos); a fonte da verdade é o `cor=` do ModuloHeader no hub.
const COR = '#0d7a74';
const COR_FORTE = '#115e59';
const COR_FUNDO = '#f0fdfa';
const COR_BORDA = '#99f6e4';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const brl = (n) => Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (iso) => String(iso || '').split('-').reverse().join('/');

const estiloInput = {
  padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8,
  fontSize: 14, color: '#0f172a', background: '#fff', fontFamily: 'inherit',
};
const th = {
  textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 800,
  color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3,
  borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap',
};
const td = { padding: '10px 12px', fontSize: 14, color: '#0f172a', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };

/** Cartão do placar. Números com `tabular-nums` para alinharem entre linhas. */
const Placar = ({ rotulo, valor, detalhe, cor }) => (
  <div style={{
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px',
    borderLeft: `4px solid ${cor}`, minWidth: 0,
  }}>
    <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3 }}>
      {rotulo}
    </div>
    <div style={{ marginTop: 6, fontSize: 24, fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
      {valor}
    </div>
    {detalhe && <div style={{ marginTop: 2, fontSize: 13, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{detalhe}</div>}
  </div>
);

/**
 * A pastilha de Sim/Não de uma linha.
 *
 * ⚠️ `riscoNoSim` existe porque a resposta de risco troca de lado entre as duas
 *    colunas: "estornado ao cliente" é ruim quando SIM; "cobrado novamente" é ruim
 *    quando NÃO. Sem o parâmetro, uma das duas pintaria a resposta boa de vermelho.
 */
const Pastilha = ({ sim, riscoNoSim = false }) => {
  const ruim = riscoNoSim ? sim : !sim;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 999,
      fontSize: 12, fontWeight: 800,
      background: ruim ? '#fee2e2' : '#dcfce7',
      color: ruim ? '#b91c1c' : '#15803d',
    }}>
      {sim ? 'Sim' : 'Não'}
    </span>
  );
};

const NulosControle = () => {
  const [opcoes, setOpcoes] = useState(null);
  const [lojas, setLojas] = useState([]);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [filtros, setFiltros] = useState({
    de: '', ate: '', loja: '', formaPagamento: '', estornado: '', cobradoNovamente: '',
    incluirCancelados: false,
  });

  // O cancelamento acontece num modal: exige motivo, e o motivo precisa de espaço.
  const [cancelando, setCancelando] = useState(null); // o registro escolhido
  const [motivo, setMotivo] = useState('');
  const [salvandoCancelamento, setSalvandoCancelamento] = useState(false);

  // --- carga inicial --------------------------------------------------
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [rOpcoes, rLojas] = await Promise.all([
          fetch(`${API}/opcoes`, { headers: auth() }),
          fetch(`${API}/lojas`, { headers: auth() }),
        ]);
        if (!vivo) return;
        if (rOpcoes.ok) setOpcoes(await rOpcoes.json());
        if (rLojas.ok) setLojas(await rLojas.json());
      } catch { /* o carregar() abaixo já reporta a falha */ }
    })();
    return () => { vivo = false; };
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const q = new URLSearchParams();
      if (filtros.de) q.set('de', filtros.de);
      if (filtros.ate) q.set('ate', filtros.ate);
      if (filtros.loja) q.set('loja', filtros.loja);
      if (filtros.formaPagamento) q.set('formaPagamento', filtros.formaPagamento);
      if (filtros.estornado) q.set('estornado', filtros.estornado);
      if (filtros.cobradoNovamente) q.set('cobradoNovamente', filtros.cobradoNovamente);
      if (filtros.incluirCancelados) q.set('incluirCancelados', '1');

      const r = await fetch(`${API}?${q}`, { headers: auth() });
      if (!r.ok) throw new Error();
      setDados(await r.json());
    } catch {
      setErro('Não foi possível carregar os registros.');
    } finally {
      setCarregando(false);
    }
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  // --- cancelamento ---------------------------------------------------
  const confirmarCancelamento = async () => {
    setSalvandoCancelamento(true);
    setErro('');
    try {
      const r = await fetch(`${API}/${cancelando.ID}/cancelar`, {
        method: 'PUT',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo }),
      });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.message || 'Não foi possível cancelar.');
      setCancelando(null);
      setMotivo('');
      carregar();
    } catch (ex) {
      setErro(ex.message);
    } finally {
      setSalvandoCancelamento(false);
    }
  };

  // --- CSV ------------------------------------------------------------
  // ⚠️ Montado NO NAVEGADOR, a partir das linhas já na tela: o que se exporta é
  //    exatamente o que se está vendo. Uma rota de export seria uma segunda
  //    consulta, capaz de divergir dos filtros aplicados aqui.
  const exportarCsv = () => {
    const linhas = dados?.registros || [];
    const cabecalho = ['ID', 'Loja', 'Data', 'Hora', 'POS', 'Gerente responsavel',
      'Forma de pagamento', 'Valor', 'Estornado', 'Cobrado novamente',
      'Registrado por', 'Cancelado em', 'Motivo do cancelamento'];
    const escapar = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const corpo = linhas.map((r) => [
      r.ID, r.LOJA, dataBr(r.DATA_OCORRENCIA), r.HORA_OCORRENCIA, r.POS, r.GERENTE_NOME,
      r.FORMA_PAGAMENTO,
      // ⚠️ Vírgula decimal: o Excel em PT-BR lê '12.50' como texto ou como data.
      String(Number(r.VALOR).toFixed(2)).replace('.', ','),
      Number(r.ESTORNADO) === 1 ? 'Sim' : 'Nao',
      Number(r.COBRADO_NOVAMENTE) === 1 ? 'Sim' : 'Nao',
      r.REGISTRADO_POR_NOME,
      r.CANCELADO_EM ? new Date(r.CANCELADO_EM).toLocaleString('pt-BR') : '',
      r.CANCELAMENTO_MOTIVO || '',
    ].map(escapar).join(';'));

    // ⚠️ BOM no começo: sem ele o Excel abre o arquivo em Latin-1 e todo
    //    acento vira caractere estranho. Escrito como o ESCAPE `\uFEFF`, nunca com
    //    o caractere literal — que é invisível no editor, some em copiar/colar
    //    entre ferramentas, e o lint acusa como espaço irregular.
    const blob = new Blob(['\uFEFF' + [cabecalho.map(escapar).join(';'), ...corpo].join('\r\n')],
      { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `nulos-progresso_${dados?.periodo?.de || ''}_a_${dados?.periodo?.ate || ''}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const t = dados?.totais;
  const registros = dados?.registros || [];

  // =====================================================================
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 22 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Controle de Nulos em Progresso</h3>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14 }}>
            Os nulos declarados pelas lojas, com a evidência anexada.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={carregar} title="Recarregar" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
            background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0',
            borderRadius: 8, fontWeight: 700, cursor: 'pointer',
          }}>
            <RefreshCw size={16} /> Atualizar
          </button>
          <button onClick={exportarCsv} disabled={!registros.length} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
            background: registros.length ? COR_FUNDO : '#f1f5f9',
            color: registros.length ? COR_FORTE : '#94a3b8',
            border: `1px solid ${registros.length ? COR_BORDA : '#e2e8f0'}`,
            borderRadius: 8, fontWeight: 700, cursor: registros.length ? 'pointer' : 'default',
          }}>
            <Download size={16} /> Exportar CSV
          </button>
        </div>
      </div>

      {/* FILTROS */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12,
        padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, marginBottom: 20,
      }}>
        <Filter size={18} color={COR} style={{ marginBottom: 9 }} />

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>De</span>
          <input type="date" value={filtros.de || dados?.periodo?.de || ''}
            onChange={(e) => setFiltros((f) => ({ ...f, de: e.target.value }))} style={estiloInput} />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Até</span>
          <input type="date" value={filtros.ate || dados?.periodo?.ate || ''}
            onChange={(e) => setFiltros((f) => ({ ...f, ate: e.target.value }))} style={estiloInput} />
        </label>

        {/* ⚠️ O seletor só aparece com mais de uma opção: para o gerente o backend
            devolve uma loja só, e um campo com uma escolha é ruído. */}
        {lojas.length > 1 && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Loja</span>
            <select value={filtros.loja} onChange={(e) => setFiltros((f) => ({ ...f, loja: e.target.value }))} style={estiloInput}>
              <option value="">Todas</option>
              {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
        )}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Forma de pagamento</span>
          <select value={filtros.formaPagamento}
            onChange={(e) => setFiltros((f) => ({ ...f, formaPagamento: e.target.value }))} style={estiloInput}>
            <option value="">Todas</option>
            {(opcoes?.formasPagamento || []).map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Estornado</span>
          <select value={filtros.estornado}
            onChange={(e) => setFiltros((f) => ({ ...f, estornado: e.target.value }))} style={estiloInput}>
            <option value="">Todos</option>
            <option value="1">Sim</option>
            <option value="0">Não</option>
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Cobrado novamente</span>
          <select value={filtros.cobradoNovamente}
            onChange={(e) => setFiltros((f) => ({ ...f, cobradoNovamente: e.target.value }))} style={estiloInput}>
            <option value="">Todos</option>
            <option value="1">Sim</option>
            <option value="0">Não</option>
          </select>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9, cursor: 'pointer' }}>
          <input type="checkbox" checked={filtros.incluirCancelados}
            onChange={(e) => setFiltros((f) => ({ ...f, incluirCancelados: e.target.checked }))} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>Mostrar cancelados</span>
        </label>
      </div>

      {erro && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: 14, marginBottom: 20,
          background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 14,
        }}>
          <AlertTriangle size={18} /> {erro}
        </div>
      )}

      {/* PLACAR — sempre do período filtrado, e sempre SEM os cancelados. */}
      {t && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginBottom: 22 }}>
          <Placar rotulo="Nulos no período" valor={t.quantidade} cor={COR} />
          <Placar rotulo="Valor total" valor={brl(t.valor)} cor="#0ea5e9" />
          <Placar rotulo="Estornados ao cliente" valor={t.quantidadeEstornados}
            detalhe={brl(t.valorEstornado)} cor="#f59e0b" />
          {/* ⚠️⚠️ O NÚMERO QUE A AUDITORIA PROCURA: estornado SIM + cobrado
              novamente NÃO = o valor voltou ao cliente e nunca foi recobrado. É o par das duas
              respostas, e por isso a conta vem do SERVIDOR — a tela não a deriva,
              senão o CSV e um relatório futuro poderiam contar diferente. */}
          <Placar rotulo="Estornado sem recobrança" valor={t.quantidadeSemCobranca}
            detalhe={brl(t.valorSemCobranca)} cor="#dc2626" />
          {/* ⚠️ Rótulo sem dono: quem cancela é o Financeiro OU a Auditoria (e o Admin/
              Diretoria). Nomear um só faria a tela mentir para o outro. */}
          <Placar rotulo="Registros cancelados" valor={t.cancelados}
            detalhe={filtros.incluirCancelados ? 'listados abaixo' : 'fora da lista'} cor="#94a3b8" />
        </div>
      )}

      {t?.truncado && (
        <div style={{ padding: 12, marginBottom: 16, background: '#fef9c3', color: '#854d0e', borderRadius: 8, fontSize: 13 }}>
          O período devolveu o teto de 2.000 registros — o placar conta só os listados. Estreite o período.
        </div>
      )}

      {/* TABELA */}
      {carregando ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#64748b', padding: 24 }}>
          <Loader2 size={18} className="spin" /> Carregando…
        </div>
      ) : registros.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
          Nenhum nulo registrado no período.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
          {/* ⚠️ O `minWidth` cresceu de 940 para 1060 quando entrou a coluna
              "Cobrado novam." (28/08): sem isso as colunas comprimem e o nome
              do gerente quebra em três linhas. Coluna nova aqui, ajuste aqui. */}
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1060 }}>
            <thead>
              <tr>
                <th style={th}>Data / hora</th>
                <th style={th}>Loja</th>
                <th style={th}>POS</th>
                <th style={th}>Gerente responsável</th>
                <th style={th}>Pagamento</th>
                <th style={{ ...th, textAlign: 'right' }}>Valor</th>
                <th style={{ ...th, textAlign: 'center' }}>Estornado</th>
                <th style={{ ...th, textAlign: 'center' }}>Cobrado novam.</th>
                <th style={{ ...th, textAlign: 'center' }}>Evidência</th>
                <th style={th}>Registrado por</th>
                {opcoes?.podeCancelar && <th style={{ ...th, textAlign: 'center' }}>Ação</th>}
              </tr>
            </thead>
            <tbody>
              {registros.map((r) => {
                const cancelado = !!r.CANCELADO_EM;
                return (
                  <tr key={r.ID} style={{ background: cancelado ? '#f8fafc' : '#fff', opacity: cancelado ? 0.65 : 1 }}>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {dataBr(r.DATA_OCORRENCIA)} <span style={{ color: '#94a3b8' }}>{r.HORA_OCORRENCIA}</span>
                      {cancelado && (
                        <div style={{ marginTop: 3, fontSize: 11, fontWeight: 800, color: '#b91c1c', textTransform: 'uppercase' }}
                          title={r.CANCELAMENTO_MOTIVO || ''}>
                          Cancelado
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, fontWeight: 700 }}>{r.LOJA}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{r.POS}</td>
                    <td style={td}>
                      {r.GERENTE_NOME}
                      {r.GERENTE_PERFIL === 'Gerente Plantao' && (
                        <span style={{ color: '#94a3b8', fontSize: 12 }}> (plantão)</span>
                      )}
                    </td>
                    <td style={td}>{r.FORMA_PAGAMENTO}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {brl(r.VALOR)}
                    </td>
                    {/* ⚠️ As duas pastilhas ficam LADO A LADO e a cor de risco
                        TROCA DE LADO entre elas: em "estornado" o vermelho é o
                        Sim; em "cobrado novamente" é o Não. Pintar igual faria a
                        linha vermelha/vermelha parecer normal. */}
                    <td style={{ ...td, textAlign: 'center' }}>
                      <Pastilha sim={Number(r.ESTORNADO) === 1} riscoNoSim />
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <Pastilha sim={Number(r.COBRADO_NOVAMENTE) === 1} />
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {/* Componente compartilhado: a webUrl do SharePoint é
                          autenticada, então os bytes vêm pelo proxy DESTE módulo. */}
                      <ImagemSharePoint apiBase={API} url={r.FOTO_URL} alt={`Nulo ${r.ID} — tela do POS`} size={48} />
                    </td>
                    <td style={{ ...td, fontSize: 13, color: '#64748b' }}>
                      {r.REGISTRADO_POR_NOME}
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {r.REGISTRADO_EM ? new Date(r.REGISTRADO_EM).toLocaleString('pt-BR') : ''}
                      </div>
                    </td>
                    {opcoes?.podeCancelar && (
                      <td style={{ ...td, textAlign: 'center' }}>
                        {!cancelado && (
                          <button onClick={() => { setCancelando(r); setMotivo(''); }} title="Cancelar este registro" style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                            background: '#fff', color: '#b91c1c', border: '1px solid #fecaca',
                            borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                          }}>
                            <Ban size={14} /> Cancelar
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL DE CANCELAMENTO */}
      {cancelando && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 26, width: '100%', maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <h4 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: '#0f172a' }}>
                Cancelar o registro nº {cancelando.ID}
              </h4>
              <button onClick={() => setCancelando(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                <X size={20} />
              </button>
            </div>

            <p style={{ margin: '0 0 16px', fontSize: 14, color: '#64748b', lineHeight: 1.55 }}>
              {cancelando.LOJA} · POS {cancelando.POS} · {brl(cancelando.VALOR)} ·{' '}
              {dataBr(cancelando.DATA_OCORRENCIA)} às {cancelando.HORA_OCORRENCIA}
              <br />
              Estornado: <strong>{Number(cancelando.ESTORNADO) === 1 ? 'Sim' : 'Não'}</strong>
              {' · '}Cobrado novamente: <strong>{Number(cancelando.COBRADO_NOVAMENTE) === 1 ? 'Sim' : 'Não'}</strong>
              <br />
              {/* A linha continua no banco: é rastro de auditoria. */}
              <span style={{ color: '#94a3b8', fontSize: 13 }}>
                O registro não é apagado — fica visível como cancelado, com seu nome e o motivo.
              </span>
            </p>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
                Motivo do cancelamento <span style={{ color: '#dc2626' }}>*</span>
              </span>
              <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} maxLength={255}
                placeholder="Ex.: lançamento em duplicidade do nulo nº 12."
                style={{ ...estiloInput, resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>{motivo.trim().length}/255 — mínimo 5 caracteres.</span>
            </label>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setCancelando(null)} style={{
                padding: '10px 18px', background: '#f1f5f9', color: '#475569',
                border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
              }}>
                Voltar
              </button>
              <button onClick={confirmarCancelamento}
                disabled={motivo.trim().length < 5 || salvandoCancelamento}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px',
                  background: motivo.trim().length < 5 ? '#cbd5e1' : '#b91c1c', color: '#fff',
                  border: 'none', borderRadius: 8, fontWeight: 700,
                  cursor: motivo.trim().length < 5 ? 'default' : 'pointer',
                }}>
                {salvandoCancelamento ? <><Loader2 size={16} className="spin" /> Cancelando…</> : <><Ban size={16} /> Confirmar cancelamento</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NulosControle;
